import { DeclineReasonPanel } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/BankAccountDetails/components/DeclineReasonPanel.tsx';
import { ReviewActions } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/BankAccountDetails/components/ReviewActions.tsx';
import { ReviewDecisionCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/BankAccountDetails/components/ReviewDecisionCard.tsx';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import {
	BankAccountReviewAction,
	BankAccountStatus,
	MipInvestorBankAccountDetailRes,
} from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import type { DeclineState } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/BankAccountDetails/components/ActionsPanel.types.ts';
import { Flex, Text } from '@mantine/core';

interface Props {
	bankAccount: MipInvestorBankAccountDetailRes;
	isReviewPending: boolean;
	onReview: (action: BankAccountReviewAction) => void;
	decline: DeclineState;
}

/** Trailing panel: the decision the reviewer can take, or the one already taken. */
export const ActionsPanel = ({ bankAccount, isReviewPending, onReview, decline }: Props) => {
	const tMipManage = useTranslationMipManage();

	return (
		<Flex
			direction="column"
			data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_BANK_ACCOUNT_ACTIONS_PANEL}
		>
			<Text size="md" fw={600} c="dark.8" p="1rem">
				{tMipManage('bank_accounts.details.actions.title', { defaultValue: 'Actions' })}
			</Text>

			<Decision
				bankAccount={bankAccount}
				isReviewPending={isReviewPending}
				onReview={onReview}
				decline={decline}
			/>
		</Flex>
	);
};

const Decision = ({ bankAccount, isReviewPending, onReview, decline }: Props) => {
	const tMipManage = useTranslationMipManage();

	if (bankAccount.review) {
		return (
			<ReviewDecisionCard
				review={bankAccount.review}
				disabled={isReviewPending}
				onRevoke={() => onReview(BankAccountReviewAction.REVOKE)}
			/>
		);
	}

	// Only an account in the review queue can be decided; anything else says why rather
	// than offering dead buttons.
	if (bankAccount.status !== BankAccountStatus.PENDING_ADMIN_REVIEW) {
		return (
			<Flex px="1rem">
				<Text size="sm" c="dark.0">
					{tMipManage('bank_accounts.details.not_awaiting_review')}
				</Text>
			</Flex>
		);
	}

	return (
		<>
			<ReviewActions disabled={isReviewPending} onReview={onReview} />
			{decline.isRejecting && (
				<DeclineReasonPanel
					reason={decline.comment}
					reasonError={decline.commentError}
					disabled={isReviewPending}
					onReasonChange={decline.handleCommentChange}
					onCancel={decline.handleCancelReject}
					onConfirm={() => void decline.handleConfirmReject()}
				/>
			)}
		</>
	);
};
